import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { LICENCE_STATES, type InspectionOutcome, type Page, type PrivateSchoolLicence } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

/**
 * Private school licensing + inspection pipeline
 * (packages/modules/m28-education/src/routes/licensing.ts). Applying is
 * citizen self-service; this console runs the ADMISSIONS_OFFICER side:
 * scheduling and recording inspections, and the approve/reject/
 * suspend/reinstate/revoke decisions. Approving creates the private School
 * record automatically (visible afterwards in Schools & Students).
 */
export function Licensing() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('ADMISSIONS_OFFICER') ?? false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('status', status);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<PrivateSchoolLicence>>(isOfficer ? `/api/v1/m28/licensing/applications?${qs.toString()}` : null);

  return (
    <div>
      <PageHeader title="Private School Licensing" subtitle="Licence applications, inspections, and the approved private-school register." />
      {!isOfficer ? (
        <ErrorNote message="Listing licence applications requires the Admissions Officer role." />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-2">
            <input className="field-input !w-56" placeholder="Search proposed school name…" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className="field-input !w-48" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All states</option>
              {LICENCE_STATES.map((s) => (
                <option key={s} value={s}>
                  {s.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </div>

          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No licence applications match" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Proposed school</th>
                    <th className="table-head">District</th>
                    <th className="table-head">Level</th>
                    <th className="table-head">Licence no.</th>
                    <th className="table-head">State</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((l) => (
                    <tr key={l.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(l.id)}>
                      <td className="table-cell font-medium">{l.proposedSchoolName}</td>
                      <td className="table-cell">{l.district}</td>
                      <td className="table-cell">
                        <StatusBadge status={l.level} />
                      </td>
                      <td className="table-cell font-mono text-xs">{l.licenceNo ?? '—'}</td>
                      <td className="table-cell">
                        <StatusBadge status={l.state} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {detailId ? <LicenceDetailModal id={detailId} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} /> : null}
    </div>
  );
}

function LicenceDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<PrivateSchoolLicence>(`/api/v1/m28/licensing/applications/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [showSchedule, setShowSchedule] = useState(false);
  const [outcome, setOutcome] = useState<InspectionOutcome>('PASS');
  const [notes, setNotes] = useState('');
  const [showRecordFor, setShowRecordFor] = useState<string | null>(null);
  const [capacity, setCapacity] = useState('60');
  const [showApprove, setShowApprove] = useState(false);
  const [reason, setReason] = useState('');
  const [showReasonFor, setShowReasonFor] = useState<'reject' | 'suspend' | 'revoke' | null>(null);

  const licence = detailQ.data;

  const act = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m28/licensing/applications/${id}${path}`, body);
      detailQ.reload();
      onChanged();
      setShowSchedule(false);
      setShowRecordFor(null);
      setShowApprove(false);
      setShowReasonFor(null);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const pendingInspection = licence?.inspections?.find((i) => !i.inspectedAt);

  return (
    <Modal title="Private school licence application" onClose={onClose} wide>
      {detailQ.loading || !licence ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center gap-2">
            <StatusBadge status={licence.state} />
            <span className="font-semibold">{licence.proposedSchoolName}</span>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">District</dt>
              <dd className="font-medium">{licence.district}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Level</dt>
              <dd className="font-medium">{licence.level}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Applicant NIN</dt>
              <dd className="font-mono text-xs">{licence.applicantNin}</dd>
            </div>
            {licence.licenceNo ? (
              <div className="flex justify-between">
                <dt className="text-slate-500">Licence no.</dt>
                <dd className="font-mono text-xs">{licence.licenceNo}</dd>
              </div>
            ) : null}
          </dl>
          {licence.rejectionReason ? <p className="text-xs text-rose-600">Rejected: {licence.rejectionReason}</p> : null}

          <div className="border-t border-slate-100 pt-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Inspections</div>
              {licence.state === 'SUBMITTED' ? (
                <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setShowSchedule((s) => !s)}>
                  + Schedule inspection
                </button>
              ) : null}
            </div>
            {showSchedule ? (
              <div className="mb-3 flex items-end gap-2 rounded-lg border border-slate-200 p-2.5">
                <Field label="Scheduled at" className="!mb-0">
                  <input type="datetime-local" className="field-input" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
                </Field>
                <button
                  className="btn-primary !px-3 !py-2 text-xs"
                  disabled={busy || !scheduledAt}
                  onClick={() => act('/schedule-inspection', { scheduledAt: new Date(scheduledAt).toISOString() })}
                >
                  Schedule
                </button>
              </div>
            ) : null}
            {(licence.inspections?.length ?? 0) === 0 ? (
              <p className="text-xs text-slate-400">No inspections yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {licence.inspections!.map((i) => (
                  <li key={i.id} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span>Scheduled {formatDateTime(i.scheduledAt)}</span>
                      {i.outcome ? <StatusBadge status={i.outcome} /> : <StatusBadge status="PENDING" />}
                    </div>
                    {i.notes ? <p className="mt-1 text-slate-500">{i.notes}</p> : null}
                    {!i.inspectedAt && licence.state === 'INSPECTION_SCHEDULED' ? (
                      showRecordFor === i.id ? (
                        <div className="mt-2 flex items-end gap-2">
                          <Field label="Outcome" className="!mb-0">
                            <select className="field-input !w-36 !py-1 text-xs" value={outcome} onChange={(e) => setOutcome(e.target.value as InspectionOutcome)}>
                              <option value="PASS">PASS</option>
                              <option value="CONDITIONAL">CONDITIONAL</option>
                              <option value="FAIL">FAIL</option>
                            </select>
                          </Field>
                          <Field label="Notes" className="!mb-0 flex-1">
                            <input className="field-input !py-1 text-xs" value={notes} onChange={(e) => setNotes(e.target.value)} />
                          </Field>
                          <button
                            className="btn-primary !px-2.5 !py-1 text-xs"
                            disabled={busy || notes.trim().length < 3}
                            onClick={() => act(`/inspections/${i.id}/record`, { outcome, notes })}
                          >
                            Record
                          </button>
                        </div>
                      ) : (
                        <button className="btn-secondary !mt-2 !px-2 !py-0.5 text-[11px]" onClick={() => setShowRecordFor(i.id)}>
                          Record outcome
                        </button>
                      )
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
            {licence.state === 'INSPECTED' && !pendingInspection ? (
              showApprove ? (
                <div className="flex items-end gap-2">
                  <Field label="Approved capacity" className="!mb-0">
                    <input className="field-input !w-32 !py-1 text-xs" type="number" min="1" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
                  </Field>
                  <button className="btn-success !px-2.5 !py-1 text-xs" disabled={busy} onClick={() => act('/approve', { capacity: Number.parseInt(capacity, 10) })}>
                    Confirm approval
                  </button>
                </div>
              ) : (
                <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => setShowApprove(true)}>
                  Approve
                </button>
              )
            ) : null}
            {['SUBMITTED', 'INSPECTION_SCHEDULED', 'INSPECTED'].includes(licence.state) ? (
              <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowReasonFor('reject')}>
                Reject
              </button>
            ) : null}
            {licence.state === 'APPROVED' ? (
              <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowReasonFor('suspend')}>
                Suspend
              </button>
            ) : null}
            {licence.state === 'SUSPENDED' ? (
              <button className="btn-success !px-2.5 !py-1 text-xs" disabled={busy} onClick={() => act('/reinstate')}>
                Reinstate
              </button>
            ) : null}
            {['APPROVED', 'SUSPENDED'].includes(licence.state) ? (
              <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowReasonFor('revoke')}>
                Revoke
              </button>
            ) : null}
          </div>

          {showReasonFor ? (
            <div className="flex items-end gap-2 rounded-lg border border-slate-200 p-3">
              <Field label="Reason" className="!mb-0 flex-1">
                <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
              <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={() => act(`/${showReasonFor}`, { reason })}>
                Confirm
              </button>
              <button className="btn-secondary" onClick={() => setShowReasonFor(null)}>
                Cancel
              </button>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
